import erc20Abi from '@abi/erc20.json'
import openCollectivePurchase from '@abi/openCollectivePurchase.json'
import rockpoolExternalAcquirer from '@abi/rockpoolExternalAcquirer.json'
import { chainConfig } from '@config'
import { MaxUint256 } from '@ethersproject/constants'
import { backendV2 } from '@graphql/ClientGraphql'
import { Erc721OpenSeaQueryData, Erc721OpenSeaQueryVars, ERC721_OPENSEA_QUERY } from '@graphql/nftfy/backend/rockpool/Erc721OpenSeaQuery'
import {
  PrivateRockpoolItemByIdData,
  PrivateRockpoolItemByIdVars,
  PRIVATE_ROCKPOOL_ITEM_BY_ID_QUERY
} from '@graphql/nftfy/backend/rockpool/PrivateRockpoolItemByIdAndSeller'
import {
  PublicRockpoolItemByIdData,
  PublicRockpoolItemByIdVars,
  PUBLIC_ROCKPOOL_ITEM_BY_ID_QUERY
} from '@graphql/nftfy/backend/rockpool/PublicRockpoolItemById'
import {
  PublicRockpoolItemByTargetData,
  PublicRockpoolItemByTargetVars,
  PUBLIC_ROCKPOOL_ITEM_BY_TARGET_QUERY
} from '@graphql/nftfy/backend/rockpool/PublicRockpoolItemByTarget'
import { clearTransaction, handleTransaction, TransactionType } from '@graphql/variables/TransactionVariable'
import { initializeWeb3 } from '@services/MultiWalletService'
import { notifyError } from '@services/NotificationService'
import { estimateGasPrice, units } from '@services/UtilService'
import axios from 'axios'
import { BigNumber } from 'bignumber.js'
import { AbiItem } from 'web3-utils'
import { RockpoolAcquirerData, RockpoolAcquirerDataVars, ROCKPOOL_ACQUIRING } from '../graphql/nftfy/backend/rockpool/Acquirer'
import { HotPoolsData, HotPoolsVars, HOT_POOLS_QUERY } from '../graphql/nftfy/backend/rockpool/HotPoolsCollectionQuery'
import { TopCollectionsData, TopCollectionsVars, TOP_COLLECTIONS_QUERY } from '../graphql/nftfy/backend/rockpool/TopCollectionQuery'
import { code } from '../messages'
import { Collection } from '../types/CollectionTypes'
import { Erc721Rockpool, RockpoolItem } from '../types/RockpoolTypes'

interface RockpoolService {
  getPublicItemById(rockpoolItemId: string, chainId: number): Promise<RockpoolItem | undefined>
  getPublicItemByTarget(collectionAddress: string, tokenId: string, chainId: number): Promise<RockpoolItem | undefined>
  getPrivateItemByIdAndSeller(rockpoolItemId: string, seller: string, chainId: number): Promise<RockpoolItem | undefined>
  leave(rockpoolItem: RockpoolItem, account: string): Promise<void>
  join(rockpoolItem: RockpoolItem, account: string, value: string): Promise<void>
  claim(rockPoolItemId: string, accountAddress: string | undefined, chainId: number): Promise<void>
  relistAndClaim(rockPoolItemId: string, accountAddress: string | undefined, chainId: number): Promise<void>
  acquired(rockPoolItemId: string, chainId: number, walletAddress: string, listed: boolean): Promise<void>
  searchNft(
    contractAddress: string,
    tokenId: string,
    chainId: number
  ): Promise<{
    rockpoolItem?: RockpoolItem
    erc721?: Erc721Rockpool
  }>
  approvedOnErc20(erc20Address: string, accountAddress: string, chainId: number): Promise<boolean>
  approveOnErc20(erc20Address: string, accountAddress: string, chainId: number): Promise<void>
  listPool(
    collectionAddress: string,
    tokenId: string | string[] | undefined,
    privatePool: boolean,
    chainId: number,
    account: string | undefined,
    name: string,
    symbol: string,
    priceMultiplier: string,
    curatorsFee: number
  ): Promise<void>
  getHotPools: (chainId: number) => Promise<RockpoolItem[]>
  getTopCollections: (chainId: number) => Promise<Collection[]>
  verifyRockpoolAvailability(rockpoolId: string, chainId: number, creator?: string): Promise<boolean>
}
export const rockpoolService = (): RockpoolService => {
  return {
    async getPublicItemById(rockpoolItemId, chainId: number): Promise<RockpoolItem | undefined> {
      const { data, error } = await backendV2.query<PublicRockpoolItemByIdData, PublicRockpoolItemByIdVars>({
        query: PUBLIC_ROCKPOOL_ITEM_BY_ID_QUERY,
        variables: {
          rockpoolItemId,
          chainId
        }
      })

      if (error) {
        throw error
      }

      if (!data || !data.publicRockpoolItem) {
        return undefined
      }

      return data.publicRockpoolItem
    },
    async getPublicItemByTarget(contractAddress: string, tokenId: string, chainId: number): Promise<RockpoolItem | undefined> {
      const { data, error } = await backendV2.query<PublicRockpoolItemByTargetData, PublicRockpoolItemByTargetVars>({
        query: PUBLIC_ROCKPOOL_ITEM_BY_TARGET_QUERY,
        variables: {
          contractAddress,
          tokenId,
          chainId
        }
      })

      if (error) {
        throw error
      }

      if (!data || !data.publicRockpoolItemByTarget) {
        return undefined
      }

      return data.publicRockpoolItemByTarget
    },
    async getPrivateItemByIdAndSeller(rockpoolItemId: string, creator: string, chainId: number): Promise<RockpoolItem | undefined> {
      const { data, error } = await backendV2.query<PrivateRockpoolItemByIdData, PrivateRockpoolItemByIdVars>({
        query: PRIVATE_ROCKPOOL_ITEM_BY_ID_QUERY,
        variables: {
          rockpoolItemId,
          chainId,
          creator
        }
      })

      if (error) {
        throw error
      }

      if (!data || !data.privateRockpoolItem) {
        return undefined
      }

      return data.privateRockpoolItem
    },
    async join(rockpoolItem: RockpoolItem, account: string, value: string): Promise<void> {
      const web3 = initializeWeb3(rockpoolItem.chainId, false)
      const config = chainConfig(rockpoolItem.chainId)
      const contract = new web3.eth.Contract(openCollectivePurchase as AbiItem[], config.rockpool?.openCollectivePurchaseContract)

      const isNetworkToken = rockpoolItem.paymentToken.id === config.networkTokenAddress
      const amountInUnits = units(value, rockpoolItem.paymentToken.decimals)

      const reservePriceInUnits = units(rockpoolItem.reservePrice || '0', rockpoolItem.paymentToken.decimals)
      contract.methods
        .join(rockpoolItem.id, amountInUnits, reservePriceInUnits)
        .send({ from: account, value: isNetworkToken ? amountInUnits : '0' }, (_error: Error, tx: string) => {
          tx ? handleTransaction(tx, TransactionType.rockpoolJoin, { observeGraphQlHash: config?.rockpool?.theGraph }) : clearTransaction()
        })
    },
    async leave(rockpoolItem: RockpoolItem, account: string): Promise<void> {
      const web3 = initializeWeb3(rockpoolItem.chainId, false)
      const config = chainConfig(rockpoolItem.chainId)
      const contract = new web3.eth.Contract(openCollectivePurchase as AbiItem[], config.rockpool?.openCollectivePurchaseContract)

      contract.methods.leave(rockpoolItem.id).send({ from: account }, (_error: Error, tx: string) => {
        tx ? handleTransaction(tx, TransactionType.rockpoolLeave, { observeGraphQlHash: config?.rockpool?.theGraph }) : clearTransaction()
      })
    },
    async claim(rockPoolItemId: string, accountAddress: string, chainId: number): Promise<void> {
      const web3 = initializeWeb3(chainId, false)
      const config = chainConfig(chainId)
      const contract = new web3.eth.Contract(openCollectivePurchase as AbiItem[], config.rockpool?.openCollectivePurchaseContract)
      contract.methods
        .claim(rockPoolItemId, accountAddress)
        .send({ from: accountAddress, gasPrice: await estimateGasPrice(chainId) }, (_error: Error, tx: string) => {
          tx ? handleTransaction(tx, TransactionType.rockPoolClaim, { observeGraphQlHash: config?.rockpool?.theGraph }) : clearTransaction()
        })
    },
    async relistAndClaim(rockPoolItemId: string, accountAddress: string, chainId: number): Promise<void> {
      const web3 = initializeWeb3(chainId, false)
      const config = chainConfig(chainId)
      const contract = new web3.eth.Contract(openCollectivePurchase as AbiItem[], config.rockpool?.openCollectivePurchaseContract)
      contract.methods
        .relistPayoutAndClaim(rockPoolItemId, [])
        .send({ from: accountAddress, gasPrice: await estimateGasPrice(chainId) }, (_error: Error, tx: string) => {
          tx ? handleTransaction(tx, TransactionType.rockPoolClaim, { observeGraphQlHash: config?.rockpool?.theGraph }) : clearTransaction()
        })
    },
    searchNft: async (
      collection: string,
      tokenId: string,
      chainId: number
    ): Promise<{
      rockpoolItem?: RockpoolItem
      erc721?: Erc721Rockpool
    }> => {
      const query = await backendV2.query<Erc721OpenSeaQueryData, Erc721OpenSeaQueryVars>({
        query: ERC721_OPENSEA_QUERY,
        fetchPolicy: 'no-cache',
        variables: {
          collection,
          tokenId,
          chainId
        }
      })

      if (query.error) {
        notifyError(code[5011], query.error)
      }

      return query.data?.erc721OpenSea
    },
    acquired: async (rockPoolItemId: string, chainId: number, walletAddress: string, listed: boolean): Promise<void> => {
      const web3 = initializeWeb3(chainId, false)
      const { data, error } = await backendV2.query<RockpoolAcquirerData, RockpoolAcquirerDataVars>({
        query: ROCKPOOL_ACQUIRING,
        variables: {
          rockpoolItemAcquiringDataId: rockPoolItemId,
          chainId,
          creator: listed ? null : walletAddress
        }
      })

      if (error) {
        throw error
      }

      if (!data || !data.rockpoolItemAcquiringData) {
        return
      }
      const config = chainConfig(chainId)
      const contract = new web3.eth.Contract(rockpoolExternalAcquirer as AbiItem[], config.rockpool?.externalAcquirerContract)
      contract.methods
        .acquire(rockPoolItemId, true, data.rockpoolItemAcquiringData.data)
        .send({ from: walletAddress }, (_error: Error, tx: string) => {
          tx
            ? handleTransaction(tx, TransactionType.rockpoolAcquireItem, { observeGraphQlHash: config?.rockpool?.theGraph })
            : clearTransaction()
        })
    },
    async approvedOnErc20(erc20Address: string, accountAddress: string, chainId: number): Promise<boolean> {
      try {
        const config = chainConfig(chainId)

        if (!config || !config.rockpool?.openCollectivePurchaseContract) {
          notifyError(code[5011], { name: 'Failed to obtain chain config', message: 'Failed to obtain chain config' })
          return false
        }

        const web3 = initializeWeb3(chainId, true)

        const contractErc20 = new web3.eth.Contract(erc20Abi as AbiItem[], erc20Address)
        const allowance = await contractErc20.methods.allowance(accountAddress, config.rockpool.openCollectivePurchaseContract).call()

        return new BigNumber(allowance).gt(0)
      } catch (e) {
        notifyError(code[5011], e)
        return false
      }
    },
    async approveOnErc20(erc20Address: string, accountAddress: string, chainId: number): Promise<void> {
      try {
        const config = chainConfig(chainId)

        if (!config || !config.rockpool?.openCollectivePurchaseContract) {
          notifyError(code[5011], { name: 'Failed to obtain chain config', message: 'Failed to obtain chain config' })
          return
        }

        const web3 = initializeWeb3(chainId, false)

        const contractErc20 = new web3.eth.Contract(erc20Abi as AbiItem[], erc20Address)
        contractErc20.methods
          .approve(config.rockpool.openCollectivePurchaseContract, MaxUint256.toString())
          .send({ from: accountAddress, gasPrice: await estimateGasPrice(chainId) }, (_error: Error, tx: string) => {
            tx ? handleTransaction(tx, TransactionType.rockpoolUnlockJoin) : clearTransaction()
          })
      } catch (e) {
        notifyError(code[5011], e)
      }
    },
    async listPool(
      collectionAddress: string,
      tokenId: string,
      privatePool: boolean,
      chainId: number,
      account: string | undefined,
      name: string,
      symbol: string,
      priceMultiplier: string,
      curatorsFee = 0
    ): Promise<void> {
      const config = chainConfig(chainId)

      const openCollectivePurchaseContract = config?.rockpool?.openCollectivePurchaseContract

      if (!config || !config?.rockpool?.openCollectivePurchaseContract) {
        throw new Error('Failed to obtain chain config')
      }

      const web3 = initializeWeb3(chainId, false)

      const contract = new web3.eth.Contract(openCollectivePurchase as AbiItem[], openCollectivePurchaseContract)

      const typeBytes = web3.utils.asciiToHex('AUCTION')

      const communityFeeUnits = units(Number(0).toString(10), 18)
      const curatorsFeePercentage = curatorsFee / 100
      const curatorsFeeUnits = units(curatorsFeePercentage.toString(10), 18)

      const duration = 259200

      const multiplier = new BigNumber(priceMultiplier).multipliedBy(100).toNumber()
      const extras = web3.eth.abi.encodeParameters(
        ['bytes32', 'string', 'string', 'uint256', 'uint256'],
        [typeBytes, name, symbol, duration, communityFeeUnits]
      )

      contract.methods
        .list(collectionAddress, tokenId, !privatePool, curatorsFeeUnits, config.networkTokenAddress, multiplier, extras)
        .send({ from: account }, (_error: Error, tx: string) => {
          tx ? handleTransaction(tx, TransactionType.rockpoolListPool) : clearTransaction()
        })
    },
    getHotPools: async (chainId: number) => {
      const result = await backendV2.query<HotPoolsData, HotPoolsVars>({
        query: HOT_POOLS_QUERY,
        variables: { chainId, limit: 5, offset: 0 }
      })

      if (!result || !result.data.rockpoolHotPoolItems) {
        return []
      }

      return result.data.rockpoolHotPoolItems
    },
    getTopCollections: async (chainId: number) => {
      const result = await backendV2.query<TopCollectionsData, TopCollectionsVars>({
        query: TOP_COLLECTIONS_QUERY,
        variables: { chainId, limit: 5, offset: 0 }
      })

      if (!result || !result.data.erc721TopCollections) {
        return []
      }

      return result.data.erc721TopCollections
    },
    async verifyRockpoolAvailability(rockpoolId: string, chainId: number, creator?: string): Promise<boolean> {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const baseUrl = process.env.NEXT_PUBLIC_BACKEND_V2_API
        // TODO: Set on env the correct base URL
        const url = creator
          ? `${baseUrl}/rockpool/update-opensea-availability/${rockpoolId}/${chainId}/${creator}`
          : `${baseUrl}/rockpool/update-opensea-availability/${rockpoolId}/${chainId}`

        await axios.get(url)

        return true
      } catch (e) {
        return false
      }
    }
  }
}
